/**
 * @fileoverview A custom web component that creates an animated confetti effect
 * using Lit Element. The confetti pieces spawn from the sides and fall with physics-based
 * animation including gravity, drag, and wobble effects.
 */

import { LitElement, html, css } from 'lit';
import { map } from 'lit/directives/map.js';

/**
 * Custom element that renders animated confetti pieces
 * @extends LitElement
 */
export class ConfettiElement extends LitElement {
  /**
   * Properties for the confetti element
   * @property {Array} colors - Array of confetti colors
   * @property {Boolean} isAnimating - Whether animation is currently running
   * @property {Boolean} autoBurst - Whether to auto-start animation on mount
   * @property {Array} _confetti - Internal array tracking confetti pieces
   */
  static get properties() {
    return {
      colors: { type: Array },
      isAnimating: { type: Boolean },
      autoBurst: { type: Boolean },
      _confetti: { type: Array, state: true },
    };
  }

  /**
   * CSS styles for the component and confetti pieces
   */
  static styles = css`
    /* Container styles */
    :host {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 9999;
      perspective: 1000px;
      overflow: hidden;
    }

    /* Base confetti piece styles */
    .confetti-piece {
      position: absolute;
      transform-origin: center;
      will-change: transform;
    }

    .circle {
      width: 10px;
      height: 10px;
      border-radius: 50%;
    }

    .square {
      width: 10px;
      height: 10px;
    }

    .rectangle {
      width: 10px;
      height: 5px;
    }

    .heart {
      width: 10px;
      height: 10px;
      background: currentColor;
      position: relative;
      transform: rotate(-45deg);
    }

    .heart::before,
    .heart::after {
      content: '';
      width: 10px;
      height: 10px;
      background: inherit;
      border-radius: 50%;
      position: absolute;
    }

    .heart::before {
      left: -5px;
    }

    .heart::after {
      top: -5px;
    }

    /* Larger sizes for desktop */
    @media (min-width: 768px) {
      .circle,
      .square {
        width: 12px;
        height: 12px;
      }

      .rectangle {
        width: 12px;
        height: 6px;
      }

      .heart {
        width: 12px;
        height: 12px;
      }

      .heart::before,
      .heart::after {
        width: 12px;
        height: 12px;
      }

      .heart::before {
        left: -6px;
      }

      .heart::after {
        top: -6px;
      }
    }
  `;

  /**
   * Initialize component properties and physics parameters
   */
  constructor() {
    super();
    this.prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
    this.isMobile = window.innerWidth < 768;
    // Define pastel color palette for confetti
    this.colors = [
      '#FFB3BA', // pastel pink
      '#BAFFC9', // pastel green
      '#BAE1FF', // pastel blue
      '#FFFFBA', // pastel yellow
      '#FFD1DC', // light pink
      '#E0BBE4', // pastel purple
    ];
    this.isAnimating = false;
    this.autoBurst = true;
    this._confetti = [];

    // Physics parameters
    this.gravity = this.isMobile ? 0.9 : 0.3; // Downward acceleration
    this.terminalVelocity = this.isMobile ? 10 : 5; // Maximum falling speed
    this.drag = 0.08; // Air resistance factor
    this.spawnHeight = 50;
  }

  /**
   * Creates a new confetti piece with randomized properties
   * @param {string} position - Starting position ('left', 'right', or 'center')
   * @returns {Object} Confetti piece configuration object
   */
  createConfettiPiece(position) {
    // Randomly select color and shape
    const color = this.colors[Math.floor(Math.random() * this.colors.length)];
    const types = ['circle', 'square', 'rectangle', 'heart'];
    const type = types[Math.floor(Math.random() * types.length)];

    let startX, startY, angle, spread;

    // 40% chance for a piece to be a "long shot" with higher initial velocity
    const isLongShot = Math.random() < 0.4;

    // Set spawn position and angle based on starting side
    switch (position) {
      case 'left':
        startX = 0;
        startY = this.spawnHeight;
        angle = isLongShot ? -30 : -45;
        spread = isLongShot ? 20 : 30;
        break;
      case 'right':
        startX = window.innerWidth - Math.random() * window.innerWidth * 0.3; // 70% to 100% of screen width
        startY = this.spawnHeight;
        angle = isLongShot ? -150 : -135;
        spread = isLongShot ? 20 : 30;
        break;
      default: // center
        startX = window.innerWidth * (0.3 + Math.random() * 0.1); // 30% to 70% of screen width
        startY = this.spawnHeight;
        angle = -90;
        spread = 45;
    }

    // Adjust speed ranges for better spread
    let speed;
    if (isLongShot) {
      speed = 30 + Math.random() * 15; // Increased long shot speed
    } else {
      const speedRoll = Math.random();
      if (speedRoll < 0.3) {
        speed = 10 + Math.random() * 5; // Slow
      } else if (speedRoll < 0.7) {
        speed = 15 + Math.random() * 8; // Medium
      } else {
        speed = 22 + Math.random() * 10; // Fast
      }
    }

    // Calculate launch angle with random spread
    const spreadAngle = (Math.random() - 0.5) * spread;
    const finalAngle = angle + spreadAngle;
    const angleRad = (finalAngle * Math.PI) / 180;

    // Return complete confetti piece configuration
    return {
      id: Math.random().toString(36).slice(2, 11),
      type,
      color,
      spawnX: startX,
      spawnY: startY,
      offset: {
        x: 0,
        y: 0,
      },
      velocity: {
        x: Math.cos(angleRad) * speed,
        y: Math.sin(angleRad) * speed,
      },
      rotation: {
        x: Math.random() * 360,
        y: Math.random() * 360,
        z: Math.random() * 360,
      },
      rotationSpeed: {
        x: (Math.random() - 0.5) * 6,
        y: (Math.random() - 0.5) * 6,
        z: (Math.random() - 0.5) * 12,
      },
      wobble: {
        x: Math.random() * Math.PI * 2,
        y: Math.random() * Math.PI * 2,
        speedX: 0.01 + Math.random() * 0.02,
        speedY: 0.01 + Math.random() * 0.02,
        amplitude: 0.3 + Math.random() * 0.5,
      },
    };
  }

  /**
   * Starts the confetti animation
   * Creates an initial burst and continues streaming for a duration
   */
  burst() {
    if (this.prefersReducedMotion) return;

    this.isAnimating = true;
    let streamDuration = 3000; // 3 seconds
    let startTime = performance.now();

    // Create initial burst of confetti
    this.addConfettiPieces();

    // Continue adding pieces over time for streaming effect
    const streamInterval = setInterval(() => {
      const elapsed = performance.now() - startTime;

      if (elapsed < streamDuration) {
        this.addConfettiPieces();
      } else {
        clearInterval(streamInterval);
      }
    }, 50); // Add new pieces every 50ms

    this.animate();
  }

  /**
   * Adds new confetti pieces to the animation
   * Creates streams from both left and right sides
   */
  addConfettiPieces() {
    const piecesPerSide = this.isMobile ? 1 : 2; // Reduce to 1 piece per side on mobile

    this._confetti = [
      ...this._confetti,
      ...Array.from({ length: piecesPerSide }, () =>
        this.createConfettiPiece('left')
      ),
      ...Array.from({ length: piecesPerSide }, () =>
        this.createConfettiPiece('right')
      ),
      ...Array.from({ length: piecesPerSide }, () =>
        this.createConfettiPiece('center')
      ),
    ];
  }

  /**
   * Main animation loop that updates confetti piece positions
   * Applies physics calculations including gravity, drag, and wobble
   */
  animate() {
    if (!this.isAnimating) return;

    this._confetti = this._confetti.filter((piece) => {
      // Apply gravity with terminal velocity limit
      piece.velocity.y += this.gravity;
      piece.velocity.y = Math.min(piece.velocity.y, this.terminalVelocity);

      // Apply drag force
      piece.velocity.x *= 1 - this.drag;
      piece.velocity.y *= 1 - this.drag;

      // Update wobble effect
      piece.wobble.x += piece.wobble.speedX;
      piece.wobble.y += piece.wobble.speedY;

      // Update position with velocity and wobble
      piece.offset.x +=
        piece.velocity.x + Math.sin(piece.wobble.x) * piece.wobble.amplitude;
      piece.offset.y += piece.velocity.y;

      // Update rotation
      piece.rotation.x += piece.rotationSpeed.x;
      piece.rotation.y += piece.rotationSpeed.y;
      piece.rotation.z += piece.rotationSpeed.z;

      // Remove pieces that have fallen below screen
      return piece.offset.y <= window.innerHeight;
    });

    this.requestUpdate();

    // Continue animation if pieces remain
    if (this._confetti.length > 0) {
      requestAnimationFrame(() => this.animate());
    } else {
      this.isAnimating = false;
    }
  }

  /**
   * Lifecycle callback when component is first updated
   * Starts animation if autoBurst is enabled
   */
  firstUpdated() {
    if (this.autoBurst) {
      // Wait for document to be fully loaded before starting animation
      if (document.readyState === 'complete') {
        this.burst();
      } else {
        window.addEventListener('load', () => this.burst(), { once: true });
      }
    }
  }

  /**
   * Renders a single confetti piece
   * @param {Object} piece - Confetti piece configuration
   * @returns {TemplateResult} Rendered HTML template
   */
  renderPiece(piece) {
    const style = `
      position: absolute;
      left: ${piece.spawnX}px;
      top: ${piece.spawnY}px;
      transform: translate3d(
        ${piece.offset.x}px, 
        ${piece.offset.y}px, 
        0
      )
      rotateX(${piece.rotation.x}deg)
      rotateY(${piece.rotation.y}deg)
      rotateZ(${piece.rotation.z}deg);
      background-color: ${piece.color};
    `;

    return html`
      <div class="confetti-piece ${piece.type}" style=${style}></div>
    `;
  }

  /**
   * Main render method for the component
   * @returns {TemplateResult} Rendered HTML template
   */
  render() {
    return html` ${map(this._confetti, (piece) => this.renderPiece(piece))} `;
  }
}

// Register the custom element
customElements.define('confetti-element', ConfettiElement);
